import React, { useEffect, useState, useCallback } from "react";
import { Loader2, TrendingUp, TrendingDown, Download, ShieldOff, ShieldCheck } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import api from "../../lib/api";
import { isExcluded, setExcluded } from "../../lib/analytics";

const GOLD = "#D4AF37";
const CHARCOAL = "#0F172A";

const PRESETS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "last_7", label: "Last 7 Days" },
  { key: "last_30", label: "Last 30 Days" },
  { key: "this_week", label: "This Week" },
  { key: "last_week", label: "Last Week" },
  { key: "this_month", label: "This Month" },
  { key: "last_month", label: "Last Month" },
  { key: "this_year", label: "This Year" },
  { key: "all_time", label: "All Time" },
  { key: "custom", label: "Custom Range" },
];
const GROUPS = ["day", "week", "month", "year"];
const METRICS = [
  { key: "visits", label: "Visits" },
  { key: "unique_visitors", label: "Unique Visitors" },
  { key: "page_views", label: "Page Views" },
  { key: "conversions", label: "Conversions" },
];

const card = "rounded-2xl border border-slate-200 bg-white p-5";
const sel = "rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-gold bg-white";

function Delta({ v }) {
  if (v === null || v === undefined) return <span className="text-xs text-slate-400">— no prior data</span>;
  const up = v >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${up ? "text-emerald" : "text-crimson"}`}>
      <Icon className="w-3.5 h-3.5" /> {up ? "↑" : "↓"} {Math.abs(v)}% <span className="text-slate-400 font-normal">vs prev</span>
    </span>
  );
}

function KpiCard({ label, value, delta, testid, suffix }) {
  return (
    <div data-testid={testid} className={card}>
      <div className="eyebrow text-slate-500">{label}</div>
      <div className="mt-1 font-serif text-3xl text-charcoal">{value}{suffix || ""}</div>
      <div className="mt-1"><Delta v={delta} /></div>
    </div>
  );
}

export default function AnalyticsPanel() {
  const [preset, setPreset] = useState("last_30");
  const [group, setGroup] = useState("day");
  const [metric, setMetric] = useState("visits");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [excluded, setExcludedState] = useState(isExcluded());

  const load = useCallback(() => {
    setLoading(true);
    const params = { preset, group };
    if (preset === "custom") { params.start = start; params.end = end; }
    api.get("/admin/analytics", { params })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load analytics"))
      .finally(() => setLoading(false));
  }, [preset, group, start, end]);

  useEffect(() => {
    if (preset === "custom" && (!start || !end)) return;
    load();
  }, [load, preset, start, end]);

  const exportCsv = async () => {
    try {
      const params = { preset };
      if (preset === "custom") { params.start = start; params.end = end; }
      const r = await api.get("/admin/analytics/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "taste-of-platinum-analytics.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const toggleExclude = () => {
    const next = !excluded;
    setExcluded(next); setExcludedState(next);
    toast.success(next ? "Your traffic is now excluded from analytics on this browser." : "Your traffic will now be counted again.");
  };

  const o = data?.overview || {};
  const fmt = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString());
  const rate = (n) => (n === null || n === undefined ? "—" : `${n}%`);

  return (
    <div data-testid="analytics-panel" className="space-y-5">
      {/* Controls */}
      <div className={`${card} flex flex-wrap items-center gap-3`}>
        <select data-testid="analytics-preset" className={sel} value={preset} onChange={(e) => setPreset(e.target.value)}>
          {PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
        {preset === "custom" && (
          <div className="flex items-center gap-2" data-testid="analytics-custom-range">
            <input data-testid="analytics-start" type="date" className={sel} value={start} onChange={(e) => setStart(e.target.value)} />
            <span className="text-slate-400">to</span>
            <input data-testid="analytics-end" type="date" className={sel} value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
        )}
        <div className="flex items-center gap-1 ml-auto">
          <button onClick={toggleExclude} data-testid="analytics-exclude-toggle"
            className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold ${excluded ? "border-crimson/40 text-crimson bg-crimson/5" : "border-slate-300 text-slate-600 hover:bg-bone"}`}>
            {excluded ? <ShieldOff className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
            {excluded ? "My traffic excluded" : "Exclude my traffic"}
          </button>
          <button onClick={exportCsv} data-testid="analytics-export" className="inline-flex items-center gap-2 rounded-full bg-charcoal px-4 py-2 text-sm font-semibold text-white hover:bg-charcoal/90">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {loading || !data ? (
        <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin text-gold mx-auto" /></div>
      ) : (
        <>
          <p className="text-xs text-slate-400" data-testid="analytics-range-note">
            Showing {data.range.start} → {data.range.end} · Times in {data.range.timezone} · Session window {data.range.session_timeout_min} min ·
            Website traffic is tracked from go-live; conversions include your recorded orders/inquiries by their real dates.
          </p>

          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard testid="kpi-visits" label="Total Visits" value={fmt(o.visits)} delta={o.deltas?.visits} />
            <KpiCard testid="kpi-unique" label="Unique Visitors" value={fmt(o.unique_visitors)} delta={o.deltas?.unique_visitors} />
            <KpiCard testid="kpi-pageviews" label="Page Views" value={fmt(o.page_views)} delta={o.deltas?.page_views} />
            <KpiCard testid="kpi-conversions" label="Orders / Inquiries" value={fmt(o.conversions)} delta={o.deltas?.conversions} />
            <KpiCard testid="kpi-rate" label="Conversion Rate" value={rate(o.conversion_rate)} delta={o.deltas?.conversion_rate} />
            <KpiCard testid="kpi-returning" label="Returning Visitors" value={fmt(o.returning_visitors)} delta={null} />
            <KpiCard testid="kpi-avg-pages" label="Avg Pages / Visit" value={fmt(o.avg_pages_per_visit)} delta={null} />
          </div>

          {/* Trend chart */}
          <div className={card} data-testid="analytics-chart">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h3 className="font-serif text-xl text-charcoal">Traffic Trend</h3>
              <div className="flex items-center gap-2">
                <select data-testid="analytics-metric" className={sel} value={metric} onChange={(e) => setMetric(e.target.value)}>
                  {METRICS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                </select>
                <select data-testid="analytics-group" className={sel} value={group} onChange={(e) => setGroup(e.target.value)}>
                  {GROUPS.map((g) => <option key={g} value={g}>{g[0].toUpperCase() + g.slice(1)}</option>)}
                </select>
              </div>
            </div>
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <AreaChart data={data.timeseries} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gGold" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={GOLD} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={GOLD} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94A3B8" }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} minTickGap={20} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94A3B8" }} tickLine={false} axisLine={false} width={40} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  <Area type="monotone" dataKey={metric} stroke={GOLD} strokeWidth={2.5} fill="url(#gGold)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-5">
            {/* Funnel */}
            <div className={card} data-testid="analytics-funnel">
              <h3 className="font-serif text-xl text-charcoal mb-4">Conversion Funnel</h3>
              <div className="space-y-3">
                {data.funnel.map((s, i) => {
                  const max = data.funnel[0].value || 1;
                  const w = Math.max(6, Math.round((s.value / max) * 100));
                  return (
                    <div key={i} data-testid={`funnel-stage-${i}`}>
                      <div className="flex justify-between text-sm text-charcoal">
                        <span>{s.stage}</span>
                        <span className="font-semibold">{s.value.toLocaleString()}{s.pct_of_prev !== null && s.pct_of_prev !== undefined ? <span className="text-slate-400 font-normal ml-1">({s.pct_of_prev}%)</span> : null}</span>
                      </div>
                      <div className="mt-1 h-3 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${w}%`, background: i === 2 ? "#065F46" : GOLD }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* By service */}
            <div className={card} data-testid="analytics-by-service">
              <h3 className="font-serif text-xl text-charcoal mb-3">Conversions by Service</h3>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-slate-400 text-xs"><th className="pb-2">Service</th><th className="pb-2 text-right">Page Views</th><th className="pb-2 text-right">Conversions</th><th className="pb-2 text-right">Rate</th></tr></thead>
                <tbody>
                  {data.by_service.map((r) => (
                    <tr key={r.service} data-testid={`service-row-${r.service}`} className="border-t border-slate-100">
                      <td className="py-2 text-charcoal">{r.label}{r.shared_page ? <span className="text-slate-400">*</span> : ""}</td>
                      <td className="py-2 text-right">{r.page_views.toLocaleString()}</td>
                      <td className="py-2 text-right font-semibold">{r.conversions.toLocaleString()}</td>
                      <td className="py-2 text-right">{rate(r.conversion_rate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-2 text-xs text-slate-400">* Catering & Private Chef share the Services page, so page views are shown for the shared page.</p>
            </div>

            {/* Top pages */}
            <div className={card} data-testid="analytics-top-pages">
              <h3 className="font-serif text-xl text-charcoal mb-3">Top Pages</h3>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-slate-400 text-xs"><th className="pb-2">Page</th><th className="pb-2 text-right">Views</th><th className="pb-2 text-right">Unique</th><th className="pb-2 text-right">Conv.</th></tr></thead>
                <tbody>
                  {data.top_pages.length === 0 && <tr><td colSpan={4} className="py-4 text-slate-400">No traffic yet.</td></tr>}
                  {data.top_pages.map((p) => (
                    <tr key={p.path} className="border-t border-slate-100">
                      <td className="py-2 text-charcoal">{p.page}</td>
                      <td className="py-2 text-right">{p.views.toLocaleString()}</td>
                      <td className="py-2 text-right">{p.unique_visitors.toLocaleString()}</td>
                      <td className="py-2 text-right">{p.conversions ? p.conversions.toLocaleString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Traffic sources */}
            <div className={card} data-testid="analytics-sources">
              <h3 className="font-serif text-xl text-charcoal mb-3">Traffic Sources</h3>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-slate-400 text-xs"><th className="pb-2">Source</th><th className="pb-2 text-right">Visits</th><th className="pb-2 text-right">Conv.</th><th className="pb-2 text-right">Rate</th></tr></thead>
                <tbody>
                  {data.sources.length === 0 && <tr><td colSpan={4} className="py-4 text-slate-400">No traffic yet.</td></tr>}
                  {data.sources.map((s) => (
                    <tr key={s.source} className="border-t border-slate-100">
                      <td className="py-2 text-charcoal">{s.source}</td>
                      <td className="py-2 text-right">{s.visits.toLocaleString()}</td>
                      <td className="py-2 text-right">{s.conversions.toLocaleString()}</td>
                      <td className="py-2 text-right">{rate(s.conversion_rate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Campaigns */}
            <div className={card} data-testid="analytics-campaigns">
              <h3 className="font-serif text-xl text-charcoal mb-3">Campaigns (UTM)</h3>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-slate-400 text-xs"><th className="pb-2">Campaign</th><th className="pb-2">Source</th><th className="pb-2 text-right">Visits</th><th className="pb-2 text-right">Conv.</th></tr></thead>
                <tbody>
                  {data.campaigns.length === 0 && <tr><td colSpan={4} className="py-4 text-slate-400">No campaign (UTM-tagged) traffic yet.</td></tr>}
                  {data.campaigns.map((c, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="py-2 text-charcoal">{c.campaign}</td>
                      <td className="py-2 text-slate-500">{c.source || "—"}</td>
                      <td className="py-2 text-right">{c.visits.toLocaleString()}</td>
                      <td className="py-2 text-right">{c.conversions.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Devices */}
            <div className={card} data-testid="analytics-devices">
              <h3 className="font-serif text-xl text-charcoal mb-3">Device Breakdown</h3>
              <div className="space-y-3">
                {data.devices.length === 0 && <p className="text-slate-400 text-sm">No traffic yet.</p>}
                {data.devices.map((d) => (
                  <div key={d.device}>
                    <div className="flex justify-between text-sm text-charcoal"><span>{d.device}</span><span className="font-semibold">{d.visits.toLocaleString()} <span className="text-slate-400 font-normal">({d.pct}%)</span></span></div>
                    <div className="mt-1 h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-gold" style={{ width: `${d.pct}%` }} /></div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent conversions */}
          <div className={card} data-testid="analytics-recent">
            <h3 className="font-serif text-xl text-charcoal mb-3">Recent Conversions</h3>
            <div className="space-y-2">
              {data.recent_conversions.length === 0 && <p className="text-slate-400 text-sm">No recorded orders/inquiries in this range.</p>}
              {data.recent_conversions.map((c, i) => (
                <div key={i} data-testid={`recent-conv-${i}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-100 bg-bone px-4 py-2.5 text-sm">
                  <span className="font-semibold text-charcoal">{c.type} — <span className="font-mono">{c.ref}</span></span>
                  <span className="text-slate-500">{c.ts}{c.source ? ` · ${c.source}` : ""}{c.campaign ? ` · ${c.campaign}` : ""}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
